import { mapState, mapActions } from 'vuex';

import { useDialog } from '@mixins/factories';

import { wrapToLoadingFn } from '@helpers';

import { namespace as LocationsNamespace } from '@modules/locations/store.types';

import {
  namespace as UsersNamespace,
  ActionTypes as UsersActionTypes,
} from '@modules/users/store.types';

import {
  namespace as ApplicationsNamespace,
  ActionTypes as ApplicationsActionTypes,
} from '../../store.type';

import ApplicationsApiService from '../../_services/applications.api.service';

const headers = Object.freeze([
  { text: 'Name', value: 'name' },
  { text: 'Email', value: 'email' },
  { text: 'Role', value: 'role' },
  { text: 'Location', value: 'location' },
]);

export default {
  name: 'UmsApplicationUsers',
  props: {
    app: {
      type: String,
      default: '',
    },
    roles: {
      type: Array,
      default: () => [],
    },
  },
  mixins: [
    useDialog({ openHandlerName: 'getApplicationUsers' }),
  ],
  data: () => ({
    headers,
    loc: '',
    role: '',
    email: '',
    isLoading: false,
    isAddLoading: false,
  }),
  computed: {
    ...mapState(ApplicationsNamespace, ['applicationUsers']),
    ...mapState(UsersNamespace, ['usersEmailsAutocomplete']),
    ...mapState(LocationsNamespace, {
      locations: ({ locations: items }) => items.map(({ name, route }) => ({
        text: `${name} (${route})`,
        value: route,
      })),
    }),
    title() {
      return `Users for application ${this.app}`;
    },
    formError() {
      const error = this.checkAlreadyUsedPreset();
      return { error, msg: error ? ['This user rights set is already in use'] : [] };
    },
    isInvalidForm() {
      return !this.email || !this.role || this.formError.error;
    },
  },
  methods: {
    ...mapActions(ApplicationsNamespace, {
      getApplicationUsers(dispatch) {
        this.wrapToLoadingFn({
          req: dispatch.bind({}, ApplicationsActionTypes.GET_APPLICATION_USERS, { app: this.app }),
          loadingFlagName: 'isLoading',
        });
      },
    }),
    ...mapActions(UsersNamespace, {
      getUsersEmailsAutocomplete: UsersActionTypes.GET_USERS_EMAILS_AUTOCOMPLETE,
    }),
    wrapToLoadingFn,
    addUser() {
      this.wrapToLoadingFn({
        req: ApplicationsApiService.addUserToApplication.bind({}, {
          app: this.app,
          data: {
            email: this.email,
            role: this.role,
            location: this.loc || null,
          },
        }),
        loadingFlagName: 'isAddLoading',
        onSuccess: () => {
          this.$VBlackerTheme.notification.success('User successfully added');
          this.getApplicationUsers();
          this.$emit('success');
          this.resetFormData();
        },
      });
    },
    removeUser(data) {
      this.$VBlackerTheme.alert.warning({
        text: `Remove ${data.name}?`,
      }, () => this.wrapToLoadingFn({
        req: ApplicationsApiService.removeUserFromApplication.bind({}, {
          app: this.app,
          data: {
            email: data.email,
            role: data.role,
            location: data.location || null,
          },
        }),
        fullscreenLoadingSpinner: true,
        onSuccess: () => {
          this.$VBlackerTheme.notification.success('User successfully removed');
          this.getApplicationUsers();
          this.$emit('success');
        },
      }));
    },
    checkAlreadyUsedPreset() {
      const keys = ['email', 'role', 'location'];
      return this.applicationUsers
        .some((user) => keys
          .every((key) => {
            if (key === 'location') {
              return (user.location || null) === (this.loc || null);
            }

            return user[key] === this[key];
          }));
    },
    resetFormData() {
      this.loc = '';
      this.role = '';
      this.email = '';
    },
  },
};
